// Salaries.jsx - Redux version
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Card, Accordion, Col, Row, Table, Alert, Spinner } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaHandHoldingUsd, FaChartLine, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import {
  PageContainer,
  PageHeader,
  InfoCard,
  AnimatedSection,
  accordionStyles
} from '../../components/SharedComponents';
import { listCurrentSalaries } from '../../actions/salaryActions';

function Salaries() {
  const dispatch = useDispatch();

  const salaryList = useSelector(state => state.salaryList);
  const { loading, error, periodData, nettoSalaries, bruttoData } = salaryList;

  useEffect(() => {
    dispatch(listCurrentSalaries());
  }, [dispatch]);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  if (loading) {
    return (
      <PageContainer>
        <Container className="text-center py-5">
          <Spinner animation="border" variant="light" style={{ width: '3rem', height: '3rem' }} />
          <p className="text-white mt-3">Ładowanie danych o wynagrodzeniach...</p>
        </Container>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Container>
          <Alert variant="danger" className="mt-4">
            <Alert.Heading>Błąd</Alert.Heading>
            <p>{error}</p>
          </Alert>
        </Container>
      </PageContainer>
    );
  }

  if (!nettoSalaries || !bruttoData) {
    return (
      <PageContainer>
        <Container>
          <Alert variant="warning" className="mt-4">
            <Alert.Heading>Brak danych</Alert.Heading>
            <p>Nie znaleziono aktywnego okresu wynagrodzeń.</p>
          </Alert>
        </Container>
      </PageContainer>
    );
  }

  const sections = [
    {
      id: 'netto',
      icon: <FaHandHoldingUsd size={24} />,
      title: 'Wynagrodzenie NETTO (na rękę)',
      color: '#43e97b',
      content: (
        <>
          {periodData.firstPaymentDate && periodData.lastPaymentDate && (
            <InfoCard
              gradient="rgba(67, 233, 123, 0.2)"
              borderColor="#43e97b"
              className="mb-4"
            >
              <div className="d-flex align-items-start">
                <FaExclamationTriangle size={24} className="me-3 mt-1" style={{ color: '#43e97b', flexShrink: 0 }} />
                <div>
                  <h6 className="fw-bold text-white mb-2">📅 Ważne informacje o płatnościach</h6>
                  <p className="text-white mb-0">
                    Pierwsza płatność w poniższej stawce nastąpi <strong>{periodData.firstPaymentDate}</strong>, ostatnia <strong>{periodData.lastPaymentDate}</strong>
                  </p>
                </div>
              </div>
            </InfoCard>
          )}

          {periodData && (
            <h6 className="fw-bold mb-3 text-white">
              Okres: {periodData.start_date} - {periodData.end_date}
            </h6>
          )}

          <Row className="g-3">
            {nettoSalaries.map((item, index) => (
              <Col xs={12} md={6} key={index}>
                <Card 
                  className="border-0 h-100" 
                  style={{ 
                    background: `linear-gradient(135deg, ${item.color}20 0%, ${item.color}10 100%)`,
                    borderRadius: '15px',
                    border: `2px solid ${item.color}40`
                  }}
                >
                  <Card.Body className="p-4 text-center">
                    <div 
                      className="badge mb-3" 
                      style={{ 
                        background: item.color, 
                        color: 'white', 
                        fontSize: '0.9rem',
                        padding: '0.5rem 1rem'
                      }}
                    >
                      {item.percentage}
                    </div>
                    <h6 className="fw-bold text-white mb-3">{item.type}</h6>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: item.color }}>
                      {item.amount}
                    </div>
                    <small className="text-white-50">miesięcznie</small>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )
    },
    {
      id: 'brutto',
      icon: <FaChartLine size={24} />,
      title: 'Wynagrodzenie BRUTTO (ze składkami)',
      color: '#667eea',
      content: (
        <>
          <p className="text-white mb-4">Poniżej szczegółowe wyliczenie wynagrodzeń brutto wraz ze składkami na ubezpieczenia społeczne dla każdego roku nauki.</p>

          {bruttoData.map((data, index) => (
            <div key={index} className="mb-5">
              <div 
                className="p-3 mb-3" 
                style={{ 
                  background: `linear-gradient(135deg, ${data.color}40 0%, ${data.color}20 100%)`,
                  borderRadius: '15px',
                  border: `2px solid ${data.color}60`
                }}
              >
                <h5 className="text-white fw-bold mb-0">
                  {data.year} - {data.netto}
                </h5>
              </div>

              <div className="table-responsive">
                <Table className="text-white mb-0" style={{ background: 'transparent' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid rgba(255,255,255,0.2)' }}>
                      <th className="text-white-50 py-3">Rodzaj ubezpieczenia</th>
                      <th className="text-white-50 py-3">Podstawa wymiaru</th>
                      <th className="text-white-50 py-3">Płatnik</th>
                      <th className="text-white-50 py-3">Ubezpieczony</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <td className="py-3">Emerytalne</td>
                      <td className="py-3">{data.podstawa}</td>
                      <td className="py-3">{data.emerytalne.platnik}</td>
                      <td className="py-3">{data.emerytalne.ubezpieczony}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <td className="py-3">Rentowe</td>
                      <td className="py-3">-</td>
                      <td className="py-3">{data.rentowe.platnik}</td>
                      <td className="py-3">{data.rentowe.ubezpieczony}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <td className="py-3">Chorobowe</td>
                      <td className="py-3">-</td>
                      <td className="py-3">{data.chorobowe.platnik}</td>
                      <td className="py-3">{data.chorobowe.ubezpieczony}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <td className="py-3">Wypadkowe</td>
                      <td className="py-3">-</td>
                      <td className="py-3">{data.wypadkowe.platnik}</td>
                      <td className="py-3">{data.wypadkowe.ubezpieczony}</td>
                    </tr>
                    <tr>
                      <td className="py-3">Zdrowotne</td>
                      <td className="py-3">{data.podstawaZdrowotna}</td>
                      <td className="py-3">{data.zdrowotne.platnik}</td>
                      <td className="py-3">{data.zdrowotne.ubezpieczony}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>
          ))}
        </>
      )
    },
    {
      id: 'notes',
      icon: <FaInfoCircle size={24} />,
      title: 'Uwagi i wyjaśnienia',
      color: '#f093fb',
      content: (
        <>
          <div className="p-3 mb-3" style={{ background: 'rgba(102, 126, 234, 0.1)', borderRadius: '10px' }}>
            <p className="text-white mb-0">
              <strong>*</strong> Stopa procentowa składki na ubezpieczenie wypadkowe ustalana jest zgodnie z rozporządzeniem Ministra Pracy i Polityki Społecznej z dnia 29 listopada 2002 r. (Dz. U. z 2022 r. poz. 740 ze zm.).
            </p>
          </div>

          <div className="p-3 mb-3" style={{ background: 'rgba(240, 147, 251, 0.1)', borderRadius: '10px' }}>
            <p className="text-white mb-0">
              <strong>**</strong> Przy założeniu, że młodocianemu przysługują <strong>podstawowe koszty uzyskania przychodów (250 zł)</strong> i złożył pracodawcy oświadczenie o stosowaniu kwoty zmniejszającej zaliczkę na podatek dochodowy w wysokości 300 zł.
            </p>
          </div>

          <div className="p-3" style={{ background: 'rgba(79, 172, 254, 0.1)', borderRadius: '10px' }}>
            <p className="text-white mb-0">
              <strong>***</strong> Przy założeniu, że młodocianemu przysługują <strong>podwyższone koszty uzyskania przychodów (300 zł)</strong> i złożył pracodawcy oświadczenie o stosowaniu kwoty zmniejszającej zaliczkę na podatek dochodowy w wysokości 300 zł.
            </p>
          </div>
        </>
      )
    }
  ];

  return (
    <PageContainer>
      <Container>
        <style>{accordionStyles}</style>

        <PageHeader 
          title="Stawki Wynagrodzenia Młodocianych"
          subtitle={periodData ? `Aktualne stawki obowiązujące od ${periodData.start_date} do ${periodData.end_date}` : 'Aktualne stawki wynagrodzenia'}
        />

        <Row className="justify-content-center">
          <Col xs={12} lg={10}>
            <Accordion alwaysOpen>
              {sections.map((section, index) => (
                <motion.div
                  key={section.id}
                  {...fadeInUp}
                  transition={{ delay: index * 0.1 }}
                  className="mb-3"
                >
                  <Accordion.Item
                    eventKey={String(index)}
                    style={{
                      border: '2px solid rgba(255,255,255,0.2)',
                      borderRadius: '20px',
                      overflow: 'hidden',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
                      background: 'rgba(255,255,255,0.03)',
                      scrollMarginTop: '20px'
                    }}
                  >
                    <Accordion.Header
                      style={{
                        borderRadius: '20px'
                      }}
                      onClick={(e) => {
                        setTimeout(() => {
                          const element = e.currentTarget?.closest('.accordion-item');
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }, 100);
                      }}
                    >
                      <div className="d-flex align-items-center w-100">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle me-3"
                          style={{
                            width: '50px',
                            height: '50px',
                            background: `linear-gradient(135deg, ${section.color} 0%, ${section.color}dd 100%)`,
                            color: 'white',
                            flexShrink: 0
                          }}
                        >
                          {section.icon}
                        </div>
                        <h5 className="mb-0 fw-bold text-white">{section.title}</h5>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        backdropFilter: 'blur(10px)',
                        padding: '2rem',
                        color: 'white'
                      }}
                      className="text-white"
                    >
                      {section.content}
                    </Accordion.Body>
                  </Accordion.Item>
                </motion.div>
              ))}
            </Accordion>

            <AnimatedSection delay={0.4} className="mt-5">
              <InfoCard>
                <h5 className="fw-bold text-white mb-3">Źródło danych</h5>
                <p className="mb-3" style={{ color: 'rgba(255,255,255,0.8)' }}>
                  Dane zostały pobrane z oficjalnego serwisu GOFIN
                </p>
                <p className="mb-0" style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
                  <strong>⚠️ Pamiętaj:</strong> Dane są aktualizowane co 3 miesiące
                </p>
              </InfoCard>
            </AnimatedSection>
          </Col>
        </Row>
      </Container>
    </PageContainer>
  );
}

export default Salaries;